import { useEffect, useMemo } from 'react'

import { UseFormMethods } from 'react-hook-form'

import {
  useManualIncidentCategoriesQuery,
  useRecipientsByRuleQuery,
  useRuleDevicesQuery,
  useRuleQuery,
} from '@/graphql/generated/hooks'
import { Action, RepeatInterval } from '@/graphql/generated/schemas'

import { FormInputsT } from '../types/types'
import {
  getActionOption,
  getDateWithoutLocalTZOffset,
  getDeviceOptions,
  getEventTypeOptions,
  getFloorsOptions,
  getOption,
  getRecipientOptions,
  getRuleDeviceIds,
  getRuleEmails,
  getSelectedDays,
  getTimeDate,
  getTimeInTimeZone,
} from '../utils/utils'

export type UseRuleIProps = {
  id: string
  form: UseFormMethods<FormInputsT>
}
export const useRule = ({ id, form }: UseRuleIProps) => {
  const { setValue } = form
  const { data, loading: isLoading } = useRuleQuery({
    variables: { id },
    skip: !id,
  })
  const { data: deviceData, loading: isDeviceLoading } = useRuleDevicesQuery({
    variables: { ids: getRuleDeviceIds(data?.rule?.ruleLogics) },
    skip: !data?.rule?.ruleLogics,
  })
  const { data: recipientData, loading: isRecipientsLoading } =
    useRecipientsByRuleQuery({
      variables: { filter: { ruleId: id } },
      skip: !id,
    })
  const { data: categoriesData } = useManualIncidentCategoriesQuery()

  useEffect(() => {
    if (data && !isLoading) {
      setValue('name', data?.rule?.name)
      setValue('action', getActionOption(data?.rule?.action))
      setValue(
        'event',
        getEventTypeOptions(
          data?.rule?.ruleEventType,
          data?.rule?.manualIncidentCategoryIds,
          categoriesData
        )
      )
    }
  }, [isLoading, data, categoriesData])

  useEffect(() => {
    if (data && !isLoading) {
      setValue(
        'facility',
        getOption(deviceData?.devices?.edges?.[0].node?.facility)
      )
      setValue('floor', getFloorsOptions(deviceData))
      setValue('device', getDeviceOptions(deviceData))
    }
  }, [isDeviceLoading, deviceData])

  useEffect(() => {
    if (recipientData && !isRecipientsLoading) {
      setValue('emails', getRecipientOptions(recipientData))
    }
  }, [isRecipientsLoading, recipientData])

  const timeZone = deviceData?.devices?.edges?.[0].node?.facility?.timeZone
  const facilityName = deviceData?.devices?.edges?.[0].node?.facility?.name
  const endTimeDate = getTimeDate(
    data?.rule?.endTimeOfDay,
    data?.rule?.startTime
  )
  const emailOptions = useMemo(
    () => getRuleEmails(recipientData),
    [recipientData]
  )

  return {
    id: id,
    days: getSelectedDays(data?.rule?.day),
    emailOptions,
    endTimeDate: getTimeInTimeZone(endTimeDate, timeZone),
    endDate: data?.rule?.endDate
      ? getDateWithoutLocalTZOffset(data?.rule?.endDate)
      : null,
    facilityName,
    name: data?.rule?.name,
    startTime: getTimeInTimeZone(data?.rule?.startTime, timeZone),
    repeatIntervalNum: data?.rule?.repeatIntervalNum || 0,
    shouldRepeat: Boolean(data?.rule?.repeatInterval),
    shouldEnd: Boolean(data?.rule?.endDate),
    shouldShowDays: data?.rule?.repeatInterval === RepeatInterval.Weekly,
    shouldShowRecipients: data?.rule?.action === Action.SendEmail,
    aiOperatorResponseRules: data?.rule?.aiOperatorResponseRules,
    timeZone,
    isLoading,
    isDeviceLoading,
    isRecipientsLoading,
    allDayOverride: data?.rule?.allDayOverride,
  }
}
